// ============================================================================
// Consultas por compañía de la app «Documentación» (react-query). Comparten la
// raíz [DOCS_QUERY_KEY, "company"] para que cualquier cambio en un checklist
// (features/shipmentDocuments/hooks) las refresque.
// ============================================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DOCS_QUERY_KEY, useDocsCompanyId } from "../../features/shipmentDocuments/hooks";
import { shipmentDocumentsService as svc, type ListQuery, type RequirementTemplate, type SubjectsQuery } from "../../services/shipmentDocuments";

const root = (companyId: string | null, ...rest: unknown[]) => [DOCS_QUERY_KEY, "company", companyId, ...rest] as const;

export function useCompanyDashboard(days = 30) {
  const companyId = useDocsCompanyId();
  return useQuery({ queryKey: root(companyId, "dashboard", days), queryFn: () => svc.dashboard(companyId, days), staleTime: 30_000 });
}

export function useCompanySubjects(query: SubjectsQuery = {}) {
  const companyId = useDocsCompanyId();
  return useQuery({ queryKey: root(companyId, "subjects", query), queryFn: () => svc.subjects(companyId, query), staleTime: 30_000 });
}

export function useCompanyList(query: ListQuery = {}, options: { enabled?: boolean } = {}) {
  const companyId = useDocsCompanyId();
  return useQuery({ queryKey: root(companyId, "list", query), queryFn: () => svc.list(companyId, query), staleTime: 30_000, enabled: options.enabled ?? true });
}

export function useCompanyExpiring(days = 30) {
  const companyId = useDocsCompanyId();
  return useQuery({ queryKey: root(companyId, "expiring", days), queryFn: () => svc.expiring(companyId, days), staleTime: 30_000 });
}

export function useCompanyRequirements() {
  const companyId = useDocsCompanyId();
  return useQuery({ queryKey: root(companyId, "requirements"), queryFn: () => svc.requirements(companyId), staleTime: 60_000 });
}

export function useSaveRequirements() {
  const companyId = useDocsCompanyId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templates: RequirementTemplate[]) => svc.saveRequirements(templates, companyId),
    onSuccess: (data) => {
      qc.setQueryData(root(companyId, "requirements"), data);
      void qc.invalidateQueries({ queryKey: [DOCS_QUERY_KEY] });
    },
  });
}
